import React, { useEffect, useRef } from "react";
import { askAiTokens as t } from "./tokens";
import { AskAiMessage } from "./types";
import UserMessage from "./UserMessage";
import AssistantMessage from "./AssistantMessage";

interface Props {
  messages: AskAiMessage[];
  thinkingLabel: string;
  copiedIdx: number | null;
  feedback: Record<number, "good" | "bad">;
  canRegenerate: boolean;
  onCopy: (text: string, idx: number) => void;
  onFeedback: (idx: number, value: "good" | "bad") => void;
  onShare: (text: string) => void;
  onRegenerate: () => void;
  onDownloadImage: (url: string, name?: string) => void;
  onSuggestedQuestion: (text: string) => void;
}

const MessageList: React.FC<Props> = ({
  messages,
  thinkingLabel,
  copiedIdx,
  feedback,
  canRegenerate,
  onCopy,
  onFeedback,
  onShare,
  onRegenerate,
  onDownloadImage,
  onSuggestedQuestion,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Index of the last assistant message — used to decide which message shows
  // the regenerate affordance.
  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      lastAssistantIdx = i;
      break;
    }
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        background: t.color.surface,
      }}
    >
      {/* Top fade so messages scrolling under the header dissolve softly */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 28,
          background:
            "linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0) 100%)",
          pointerEvents: "none",
          zIndex: 2,
        }}
      />
      <div
        ref={scrollRef}
        style={{
          height: "100%",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            maxWidth: t.layout.contentMaxWidth,
            margin: "0 auto",
            padding: `${t.space.s6}px ${t.space.s6}px ${t.space.s8}px`,
          }}
        >
        {messages.map((m, idx) =>
          m.role === "user" ? (
            <UserMessage key={idx} message={m} />
          ) : (
            <AssistantMessage
              key={idx}
              message={m}
              thinkingLabel={thinkingLabel}
              copied={copiedIdx === idx}
              feedback={feedback[idx]}
              isLastAssistant={idx === lastAssistantIdx}
              canRegenerate={canRegenerate}
              onCopy={() => onCopy(m.text, idx)}
              onFeedback={(v) => onFeedback(idx, v)}
              onShare={() => onShare(m.text)}
              onRegenerate={onRegenerate}
              onDownloadImage={
                m.imageUrl
                  ? () => onDownloadImage(m.imageUrl!, m.imageName)
                  : undefined
              }
              onSuggestedQuestion={onSuggestedQuestion}
            />
          )
        )}
        </div>
      </div>
    </div>
  );
};

export default MessageList;
